import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';

import { FilterCheckboxItem } from './FilterCheckboxItem';
import { languageModelName, languageModelSearchTerms } from './filterSearchTerms';
import { SearchableCheckboxList } from './SearchableCheckboxList';
import { FilterPanelProps } from './types';

/**
 * Renders a list of available language models with checkboxes for filtering
 */
export const LanguageModelsFilter = ({
  filterIndex,
  searchText = '',
  onSearchChange = () => {},
}: FilterPanelProps) => {
  const { t } = useTranslation();
  const { enabledModelsWithMoveo, languageModelNames } = useLanguageModels();
  const { getValues, control } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  const languageModelIds =
    useWatch({ control, name: `filters.${filterIndex}.languageModelIds` }) || [];

  const handleLanguageModelToggle = (modelId: string, checked: boolean) => {
    const currentModelIds = Array.isArray(languageModelIds) ? [...languageModelIds] : [];
    const newModelIds = checked
      ? [...currentModelIds, modelId]
      : currentModelIds.filter((id: string) => id !== modelId);

    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, languageModelIds: newModelIds });
  };

  if (!enabledModelsWithMoveo || enabledModelsWithMoveo.length === 0) {
    return <FilterEmptyState text={t('analytics.filters.no_language_models')} />;
  }

  return (
    <SearchableCheckboxList
      searchText={searchText}
      onSearchChange={onSearchChange}
      items={enabledModelsWithMoveo}
      getSearchTerms={(model) => languageModelSearchTerms(model, languageModelNames)}
      getGroup={(model) => model.provider}
      placeholder={t('developer_tools.language_models_search')}
      renderItem={(model) => (
        <FilterCheckboxItem
          key={model.language_model_id}
          id={`language-model-${model.language_model_id}`}
          checked={languageModelIds.includes(model.language_model_id)}
          onCheckedChange={(checked) =>
            handleLanguageModelToggle(model.language_model_id, checked as boolean)
          }
        >
          <span className="text-sm">{languageModelName(model, languageModelNames)}</span>
        </FilterCheckboxItem>
      )}
    />
  );
};
